
import React from 'react'

import type { Certificate } from 'types'

import { useTheme, Box, Skeleton, Stack, Typography } from '@mui/material'

import { Image } from '@repo/ui'
import { Preview } from 'components'
// VRG-777 — `products.images` holds stills AND video in one ordered list, so `images[0]`
// is no longer necessarily a still. `getProductThumbnail` returns the first non-video
// entry; shared in @repo/types so this branch is not re-written at each of the ~20 sites
// that read the column (§36).
import { getProductThumbnail } from '@repo/types'


interface CertificatePreviewProps {
  certificate: Certificate | null | undefined,
  onChange?: any,
  onRemove?: any,
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

export const CertificatePreview = (props: CertificatePreviewProps) => {
  const { certificate, onChange = false, onRemove = false, disabled = false, size = 'large'} = props  
  const theme = useTheme()

  if (!certificate) return null

  return (
    <Preview size={size} item={certificate} onRemove={onRemove} onChange={onChange} disabled={disabled}>
      <Box sx={theme => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 2})}>
        <Image
          src={getProductThumbnail(certificate.product?.images) ?? ''}
          alt={certificate.product?.title}
          fit="cover"
          sx={theme => ({ 
            borderRadius: 2,
            width: size == 'large' ? 64 : size == 'medium' ? 48 : 28, 
            height: size == 'large' ? 64 : size == 'medium' ? 48 : 28 
          })}
        />
      </Box>
      <Stack spacing={2} sx={{ minWidth: 0, maxWidth: size == 'small' ? 100 : '100%' }}>
        <Typography variant="body2" noWrap>
          {certificate.product?.title}
        </Typography>
        <Typography variant="body3" color="text.secondary" noWrap>
          {certificate.id}
        </Typography>
      </Stack>
    </Preview>
  )
}

export const CertificatePreviewLoader = () => {
  return (
    <Box p={3} sx={theme => ({ border: `1px solid ${theme.palette.grey[300]}` })}>
      <Stack spacing={4} direction="row" alignItems="center">
        <Box>
          <Skeleton variant="rectangular" width={64} height={64} />
        </Box>
        <Stack spacing={2} width={'100%'}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={'100%'} />
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={'100%'} />
        </Stack>
      </Stack>
    </Box>
  )
}


export const CertificatePreviewAutoComplete = (props: CertificatePreviewProps) => {
  const { certificate } = props

  if (!certificate.product) return null

  return (
    <Box p={3} sx={theme => ({borderRadius: 2 })}>
      <Stack spacing={4} direction="row" alignItems="center">
          <Image
            src={getProductThumbnail(certificate.product?.images) ?? ''}
            alt={certificate.product?.title}
            fit="cover"
            sx={theme => ({ width: 48, height: 48, borderRadius: 2, border: `1px solid ${theme.palette.grey[300]}` })}
          />
        <Stack spacing={2}>
          <Typography variant="body2" noWrap>
            {certificate.product?.title}
          </Typography>
          <Typography variant="body3" color="text.secondary" noWrap>
            {certificate.product?.sku}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}
